// SyncSparkline — a 7-bar status track for recent sync jobs that included
// this connector. Each bar maps to one job; height encodes "did it work?",
// colour encodes status. We deliberately keep this dumb and presentational
// so it can be dropped into the header strip with no extra context.
//
// We chose bars (not a line chart) because what matters operationally is
// "was the last run green or red?" — discrete states, not a continuous
// metric. A 24-hour latency line would be misleading; a colour-coded
// sequence is honest about what data we actually have.

import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { formatRelativeTime } from '../../utils/date';

export interface SyncSparklinePoint {
  jobId: string;
  status: 'completed' | 'failed' | 'running' | 'pending' | string;
  startedAt: string;
}

interface Props {
  points: SyncSparklinePoint[];
  /** Maximum bars to show (oldest are dropped). */
  cap?: number;
  /** If true, renders a tiny right-aligned summary "x / y successful". */
  showSummary?: boolean;
}

const STATUS_COLOR: Record<string, string> = {
  completed: '#16a34a',
  failed: '#dc2626',
  running: '#2563eb',
  pending: '#94a3b8',
};

const STATUS_HEIGHT: Record<string, number> = {
  completed: 18,
  failed: 18,
  running: 14,
  pending: 10,
};

export default function SyncSparkline({ points, cap = 7, showSummary }: Props) {
  // Show oldest -> newest left -> right so the rightmost bar is "now".
  const trimmed = points.slice(0, cap).reverse();
  const successCount = trimmed.filter((p) => p.status === 'completed').length;

  if (trimmed.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.375, height: 20 }}>
          {Array.from({ length: cap }).map((_, i) => (
            <Box
              key={i}
              sx={(t) => ({
                width: 4,
                height: 6,
                borderRadius: 0.5,
                bgcolor: t.tokens.border.subtle,
              })}
            />
          ))}
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.75rem' }}>
          No syncs yet
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.375, height: 20 }}>
        {trimmed.map((p) => {
          const h = STATUS_HEIGHT[p.status] ?? 12;
          const c = STATUS_COLOR[p.status] ?? '#94a3b8';
          return (
            <Tooltip
              key={p.jobId}
              title={
                <Box sx={{ fontSize: '0.75rem', lineHeight: 1.4 }}>
                  <Box sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{p.status}</Box>
                  <Box sx={{ opacity: 0.85 }}>{formatRelativeTime(p.startedAt)}</Box>
                </Box>
              }
              arrow
            >
              <Box
                sx={{
                  width: 4,
                  height: h,
                  borderRadius: 0.5,
                  bgcolor: c,
                  transition: 'transform 120ms ease',
                  cursor: 'default',
                  '&:hover': { transform: 'scaleY(1.1)' },
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
      {showSummary && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
        >
          {successCount}/{trimmed.length} successful
        </Typography>
      )}
    </Box>
  );
}
